import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceArea,
  CartesianGrid,
  usePlotArea,
  useXAxisDomain,
  useYAxisDomain,
} from 'recharts';
import TapTooltipChart from '@/components/TapTooltipChart';
import AbbrTip from '@/components/AbbrTip';
import { Loader2, Clock, Info, Briefcase, CandlestickChart, Activity, HelpCircle, Download } from 'lucide-react';
import ShareToXButton from '@/components/ShareToXButton';
const VWAP_COLOR = 'rgb(245 158 11)';

const UP = 'rgb(16 185 129)';
const DOWN = 'rgb(244 63 94)';

const formatX = (mins) => {
  const h = Math.floor(mins / 60) + 4;
  const m = mins % 60;
  const hh = h > 12 ? h - 12 : h;
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${hh}${m === 0 ? '' : ':' + String(m).padStart(2, '0')}${ampm}`;
};

const fmtVol = (v) => (v >= 1e9 ? `${(v / 1e9).toFixed(2)}B` : v >= 1e6 ? `${(v / 1e6).toFixed(2)}M` : v >= 1e3 ? `${(v / 1e3).toFixed(1)}K` : String(v));

// Volume bars for the slim strip under the price chart. Lives in its own
// tiny LineChart with identical margins/YAxis width so X lines up 1:1.
function VolumeBars({ buckets, interval, maxVol }) {
  const plot = usePlotArea();
  const xd = useXAxisDomain();
  if (!plot || !xd || !buckets?.length || !maxVol) return null;
  const [x0, x1] = xd;
  const xs = (v) => plot.x + ((v - x0) / ((x1 - x0) || 1)) * plot.width;
  const w = Math.max(1, (xs(interval) - xs(0)) * 0.7);
  return (
    <g data-testid="intraday-volume-bars">
      {buckets.map((b) => {
        const h = Math.max(1, (b.v / maxVol) * plot.height);
        const regular = b.mins >= 330 && b.mins < 720;
        return <rect key={b.mins} x={xs(b.mins + interval / 2) - w / 2} y={plot.y + plot.height - h} width={w} height={h} fill={b.color} opacity={regular ? 0.9 : 0.35} />;
      })}
    </g>
  );
}

// Candlestick overlay. Recharts 3.6 has no scale hooks yet, so rebuild the
// two linear scales from the plot area + resolved axis domains (both axes
// are numeric here) so candles share X/Y with the line and ghost series.
function Candles({ candles, interval }) {
  const plot = usePlotArea();
  const xd = useXAxisDomain();
  const yd = useYAxisDomain();
  if (!plot || !xd || !yd || !candles?.length) return null;
  const [x0, x1] = xd;
  const [y0, y1] = yd;
  const xs = (v) => plot.x + ((v - x0) / ((x1 - x0) || 1)) * plot.width;
  const ys = (v) => plot.y + ((y1 - v) / ((y1 - y0) || 1)) * plot.height;
  const w = Math.max(1, (xs(interval) - xs(0)) * 0.7);
  return (
    <g data-testid="intraday-candles">
      {candles.map((c) => {
        const color = c.c >= c.o ? UP : DOWN;
        const x = xs(c.mins + interval / 2);
        const yO = ys(c.o);
        const yC = ys(c.c);
        return (
          <g key={c.mins}>
            <line x1={x} x2={x} y1={ys(c.h)} y2={ys(c.l)} stroke={color} strokeWidth={1} />
            <rect x={x - w / 2} y={Math.min(yO, yC)} width={w} height={Math.max(1, Math.abs(yO - yC))} fill={color} />
          </g>
        );
      })}
    </g>
  );
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Intraday "Today" chart — Round 84k Phase B (Feb 15, 2026).
 *
 * Pulls Massive's 1-minute aggregates for the ticker on the current
 * trading day and renders a 7am–8pm Eastern Time line chart with
 * shaded session bands (pre-market 4–9:30am, regular 9:30am–4pm,
 * after-hours 4–8pm).
 *
 * Hidden when the backend returns `enabled: false` (default until the
 * paid Massive plan is active). When enabled but no bars yet (early
 * pre-market, weekend, holiday), shows a friendly placeholder instead
 * of an empty chart.
 *
 * Times are stored UTC in the API response and converted to Eastern
 * Time client-side via `toLocaleTimeString({ timeZone: 'America/New_York' })`.
 */
export default function IntradayTab({ ticker, isLoggedOut = false, onSignInClick }) {
  const [bars, setBars] = useState([]);
  const [priorBars, setPriorBars] = useState([]);
  const [priorDate, setPriorDate] = useState(null);
  // Persisted via localStorage so user's preference sticks across
  // modal opens. Default ON — the whole point is "is today's move
  // breaking out of yesterday's rhythm?".
  const [showPrior, setShowPrior] = useState(() => {
    try {
      const v = localStorage.getItem('intraday-show-prior');
      return v === null ? true : v === '1';
    } catch { return true; }
  });
  // Line (close only) or Candles (5-min OHLC; 15-min on narrow screens).
  const [chartMode, setChartMode] = useState(() => {
    try { return localStorage.getItem('intraday-chart-mode') === 'candles' ? 'candles' : 'line'; } catch { return 'line'; }
  });
  useEffect(() => {
    try { localStorage.setItem('intraday-chart-mode', chartMode); } catch (e) { /* ignore */ }
  }, [chartMode]);
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.innerWidth < 640);
  useEffect(() => {
    const onR = () => setNarrow(window.innerWidth < 640);
    window.addEventListener('resize', onR);
    return () => window.removeEventListener('resize', onR);
  }, []);
  // Candle size: 1 / 5 / 15 minutes. Auto (5 desktop, 15 phone) until chosen.
  const [candlePick, setCandlePick] = useState(() => {
    try { const v = Number(localStorage.getItem('intraday-candle-interval')); return [1, 5, 15, 30].includes(v) ? v : null; } catch { return null; }
  });
  const candleInterval = candlePick || (narrow ? 15 : 5);
  // VWAP overlay — off by default, user opt-in (remembered).
  const [showVwap, setShowVwap] = useState(() => {
    try { return localStorage.getItem('intraday-show-vwap') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('intraday-show-vwap', showVwap ? '1' : '0'); } catch (e) { /* ignore */ }
  }, [showVwap]);
  const pickInterval = (v) => {
    setCandlePick(v);
    try { localStorage.setItem('intraday-candle-interval', String(v)); } catch (e) { /* ignore */ }
  };
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(true);
  const [error, setError] = useState(null);
  // Round 86 (Feb 2026) — `dataDate` is the YYYY-MM-DD the backend
  // returned, which may be yesterday/Friday during off-hours due to
  // the 4am ET active-day rollover.
  const [dataDate, setDataDate] = useState(null);
  // Round 92o++++++++N++++++++++ — Massive upstream failure flag. When
  // the backend returns `source: "fetch_failed"` it means Massive's
  // own /v2/aggs endpoint hung / timed out for this specific ticker
  // on this specific day (observed June 2026 on MRVL). Used to swap
  // the "no trades yet today" empty-state for a clearer
  // "data feed temporarily unavailable" message so users don't think
  // the ticker simply hasn't traded.
  const [dataSource, setDataSource] = useState(null);

  useEffect(() => {
    try { localStorage.setItem('intraday-show-prior', showPrior ? '1' : '0'); } catch (e) { /* ignore */ }
  }, [showPrior]);

  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await axios.get(`${API}/stocks/${ticker.toUpperCase()}/intraday`, {
          withCredentials: true,
        });
        if (cancelled) return;
        setEnabled(data?.enabled !== false);
        setBars(Array.isArray(data?.bars) ? data.bars : []);
        setPriorBars(Array.isArray(data?.prior_bars) ? data.prior_bars : []);
        setPriorDate(data?.prior_date || null);
        setDataDate(data?.date || null);
        setDataSource(data?.source || null);
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.detail || 'Could not load intraday data.');
          setBars([]);
          setPriorBars([]);
          setPriorDate(null);
          setDataDate(null);
          setDataSource(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [ticker]);

  // Round 86 (May 2026) — Live "now" tracker. Updates every 30s so the
  // pulsing-active-band + dashed "Now" vertical line stay in sync with
  // the real clock without needing a re-render storm. Returns mins
  // since 4am ET (matching the chart's X-axis offset), or null when
  // we're outside the 4am-8pm ET trading window.
  const [nowMins, setNowMins] = useState(null);
  useEffect(() => {
    const compute = () => {
      try {
        const parts = new Intl.DateTimeFormat('en-US', {
          timeZone: 'America/New_York',
          hour12: false,
          weekday: 'short',
          hour: '2-digit',
          minute: '2-digit',
        }).formatToParts(new Date());
        const wk = parts.find((p) => p.type === 'weekday')?.value;
        const h = parseInt(parts.find((p) => p.type === 'hour')?.value || '0', 10);
        const m = parseInt(parts.find((p) => p.type === 'minute')?.value || '0', 10);
        if (wk === 'Sat' || wk === 'Sun') { setNowMins(null); return; }
        const mins = (h - 4) * 60 + m;
        if (mins < 0 || mins > 960) { setNowMins(null); return; }
        setNowMins(mins);
      } catch {
        setNowMins(null);
      }
    };
    compute();
    const id = setInterval(compute, 30 * 1000);
    return () => clearInterval(id);
  }, []);

  // Active session band [x1, x2] given the current `nowMins`, or null
  // when outside the trading window. Bands: pre-mkt 0-330, regular
  // 330-720, after-hrs 720-960.
  const activeBand = useMemo(() => {
    if (nowMins == null) return null;
    if (nowMins < 330) return { x1: 0, x2: 330, label: 'Pre-Market', color: 'rgb(251 191 36)' };
    if (nowMins < 720) return { x1: 330, x2: 720, label: 'Regular Session', color: 'rgb(16 185 129)' };
    return { x1: 720, x2: 960, label: 'After-Hours', color: 'rgb(14 165 233)' };
  }, [nowMins]);

  // Round 86 — Format the "Data for [Date], 7am-8pm ET" header. Also
  // detect whether we're currently OUTSIDE the live window (i.e., the
  // session has ended and the chart is now frozen for the day).
  const headerInfo = useMemo(() => {
    if (!dataDate) return { dateLabel: '', isFrozen: false };
    try {
      // Format the data-date as a friendly "Mon, Feb 18, 2026".
      const dateObj = new Date(dataDate + 'T12:00:00Z'); // noon UTC to dodge timezone edge cases
      const dateLabel = dateObj.toLocaleDateString('en-US', {
        timeZone: 'America/New_York',
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
      // Determine current ET time to see if we're in the live window.
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        hour12: false,
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
      }).formatToParts(new Date());
      const wk = parts.find((p) => p.type === 'weekday')?.value;
      const h = parseInt(parts.find((p) => p.type === 'hour')?.value || '0', 10);
      const m = parseInt(parts.find((p) => p.type === 'minute')?.value || '0', 10);
      const mins = h * 60 + m;
      const inLiveWindow =
        wk !== 'Sat' && wk !== 'Sun'
        && mins >= 4 * 60          // 04:00 ET (full pre-market)
        && mins < 20 * 60 + 14;    // 20:14 ET
      // Also: if the data-date is NOT today's ET calendar date, the
      // chart is frozen by definition (e.g., showing Friday on Saturday).
      const todayEt = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date());
      const isFrozen = !inLiveWindow || todayEt !== dataDate;
      return { dateLabel, isFrozen };
    } catch {
      return { dateLabel: dataDate, isFrozen: false };
    }
  }, [dataDate]);
  // Helper: turn a raw `bars` array into {mins, close, ...} points
  // mapped to minutes-since-4am ET (the chart's X-axis basis).
  const buildPoints = (rawBars) => {
    const out = [];
    for (const b of rawBars || []) {
      try {
        const d = new Date(b.t_iso);
        const etHM = d.toLocaleTimeString('en-US', {
          timeZone: 'America/New_York',
          hour12: false,
          hour: '2-digit',
          minute: '2-digit',
        });
        const [h, m] = etHM.split(':').map(Number);
        const minsSince4am = (h - 4) * 60 + m;
        if (minsSince4am < 0 || minsSince4am > 960) continue;
        out.push({ mins: minsSince4am, label: etHM, close: b.c, volume: b.v, o: b.o, h: b.h, l: b.l, vw: b.vw });
      } catch { /* skip bad row */ }
    }
    return out;
  };

  const todayPoints = useMemo(() => buildPoints(bars), [bars]);
  const priorPoints = useMemo(() => buildPoints(priorBars), [priorBars]);

  // Cumulative VWAP: Σ(bar vwap × volume) / Σ volume, minute by minute
  // (Massive gives per-bar `vw`; fall back to the typical price).
  const vwapByMins = useMemo(() => {
    const m = new Map();
    let pv = 0;
    let vol = 0;
    for (const p of todayPoints) {
      const v = p.volume || 0;
      const px = p.vw > 0 ? p.vw : (p.h > 0 && p.l > 0 ? (p.h + p.l + p.close) / 3 : p.close);
      if (v > 0 && px > 0) { pv += px * v; vol += v; }
      if (vol > 0) m.set(p.mins, pv / vol);
    }
    return m;
  }, [todayPoints]);

  // Session stats chips: open (first print), high, low, total volume.
  const sessionStats = useMemo(() => {
    if (!todayPoints.length) return null;
    let hi = -Infinity;
    let lo = Infinity;
    let vol = 0;
    for (const p of todayPoints) {
      if (p.h > 0) hi = Math.max(hi, p.h);
      if (p.l > 0) lo = Math.min(lo, p.l);
      vol += p.volume || 0;
    }
    const first = todayPoints[0];
    const last = todayPoints[todayPoints.length - 1];
    const rangePct = hi > lo ? ((last.close - lo) / (hi - lo)) * 100 : null;
    const prior = priorPoints.length ? priorPoints[priorPoints.length - 1].close : null;
    return { open: first.o > 0 ? first.o : first.close, high: hi, low: lo, volume: vol, openLabel: formatX(first.mins), last: last.close, rangePct, priorClose: prior };
  }, [todayPoints, priorPoints]);

  // Round 86b (May 2026) — Merge today + yesterday into a single data
  // array keyed by `mins` so Recharts can render two `<Line>` series
  // sharing the same X-axis. Each row is `{mins, close?, priorClose?}`.
  // We sort by `mins` so the line paths draw left-to-right correctly.
  const chartData = useMemo(() => {
    const byMins = new Map();
    for (const p of todayPoints) {
      byMins.set(p.mins, { mins: p.mins, close: p.close, vwap: showVwap ? vwapByMins.get(p.mins) : undefined });
    }
    if (showPrior) {
      for (const p of priorPoints) {
        const existing = byMins.get(p.mins) || { mins: p.mins };
        existing.priorClose = p.close;
        byMins.set(p.mins, existing);
      }
    }
    return Array.from(byMins.values()).sort((a, b) => a.mins - b.mins);
  }, [todayPoints, priorPoints, showPrior, showVwap, vwapByMins]);

  // Bucket 1-min bars into `candleInterval`-minute OHLCV (candles + volume strip).
  const buckets = useMemo(() => {
    const buckets = new Map();
    for (const p of todayPoints) {
      if (!(p.o > 0 && p.h > 0 && p.l > 0 && p.close > 0)) continue;
      const k = Math.floor(p.mins / candleInterval) * candleInterval;
      const b = buckets.get(k);
      if (!b) buckets.set(k, { mins: k, o: p.o, h: p.h, l: p.l, c: p.close, v: p.volume || 0 });
      else { b.h = Math.max(b.h, p.h); b.l = Math.min(b.l, p.l); b.c = p.close; b.v += p.volume || 0; }
    }
    return Array.from(buckets.values()).sort((a, b) => a.mins - b.mins);
  }, [todayPoints, candleInterval]);
  const candles = chartMode === 'candles' ? buckets : [];
  const candleByMins = useMemo(() => new Map(buckets.map((c) => [c.mins, c])), [buckets]);
  const volumeBuckets = useMemo(() => buckets.map((b) => ({
    mins: b.mins, v: b.v,
    color: chartMode === 'candles' ? (b.c >= b.o ? UP : DOWN) : 'rgb(124 58 237)',
  })), [buckets, chartMode]);
  const maxVol = useMemo(() => Math.max(0, ...volumeBuckets.map((b) => b.v)), [volumeBuckets]);

  // Used by the empty-state check + Y-domain — semantically "do we
  // have anything to draw?".
  const points = todayPoints;

  // Round P112g (Jul 31, 2026) — Sparse pre-market liquidity hint.
  // Pre-market minute bars only exist when a trade actually prints in
  // that minute. Micro-cap tickers like BRIA can go 30+ minutes at a
  // stretch with zero pre-market prints, so the chart looks blank
  // even when the ticker is technically "live" pre-market. Detect
  // this and surface a clear explanation so users don't wonder why
  // the tab is empty at 9:00 AM ET on a thin ticker. Threshold: <10
  // pre-market bars in the whole 4:00-9:30 AM window = sparse.
  const preMarketBars = useMemo(
    () => todayPoints.filter(p => p.mins >= 0 && p.mins < 330).length,
    [todayPoints],
  );
  const isCurrentlyPreMarket = activeBand?.label === 'Pre-Market';
  const preMarketIsSparse = preMarketBars < 10 && (isCurrentlyPreMarket || preMarketBars > 0);

  // Compute Y-axis padding so neither line is flush against the edges.
  // Includes prior-day closes when the ghost line is visible so the
  // grey curve doesn't get clipped against today's narrower range.
  const yDomain = useMemo(() => {
    const values = chartMode === 'candles' && candles.length
      ? candles.flatMap((c) => [c.h, c.l])
      : todayPoints.map(p => p.close);
    if (showPrior) values.push(...priorPoints.map(p => p.close));
    if (showVwap) values.push(...vwapByMins.values());
    if (sessionStats?.priorClose > 0) values.push(sessionStats.priorClose);
    if (!values.length) return [0, 1];
    const min = Math.min(...values);
    const max = Math.max(...values);
    const pad = Math.max((max - min) * 0.05, 0.1);
    return [min - pad, max + pad];
  }, [todayPoints, priorPoints, showPrior, chartMode, candles, showVwap, vwapByMins, sessionStats]);

  if (!enabled) {
    // Tab parent should be hiding us when this happens, but as a safety
    // net render a clean "soon" state.
    return (
      <div className="p-6 text-center text-sm font-bold text-muted-foreground space-y-2" data-testid="intraday-disabled">
        <Clock className="w-6 h-6 mx-auto opacity-50" />
        <p className="font-semibold">Intraday data coming soon.</p>
        <p className="text-xs">Live minute-by-minute charts activate when the paid Massive data plan is enabled.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground" data-testid="intraday-loading">
        <Loader2 className="w-4 h-4 animate-spin mr-2" />Loading today's bars…
      </div>
    );
  }

  if (error) {
    return <div className="p-4 text-sm text-rose-600 dark:text-rose-400" data-testid="intraday-error">{error}</div>;
  }

  // Show empty state when we have no printed bars to plot. The live
  // snapshot price is intentionally NOT used as a fallback chart —
  // we keep this view purely for actual trade prints to avoid the
  // "three different prices on screen" confusion.
  if (!points.length) {
    // Round 92o++++++++N++++++++++ — distinguish a true "ticker hasn't
    // traded yet" empty state from a Massive upstream failure. The
    // backend returns `source: "fetch_failed"` when its /v2/aggs call
    // timed out (Massive's own servers were unresponsive for this
    // specific ticker on this specific day — observed on MRVL,
    // June 2026). Showing "No trades yet today" in that case is
    // misleading because the ticker IS trading; the data feed just
    // didn't return.
    if (dataSource === 'fetch_failed') {
      return (
        <div className="p-6 text-center text-sm font-bold text-muted-foreground space-y-2" data-testid="intraday-feed-unavailable">
          <Info className="w-6 h-6 mx-auto opacity-50" />
          <p>Data feed is temporarily unavailable for {(ticker || '').toUpperCase()} today.</p>
          <p className="text-xs">Our intraday provider didn&rsquo;t respond for this ticker. Other stocks in your portfolio should still load normally. Try again in a few minutes.</p>
        </div>
      );
    }
    return (
      <div className="p-6 text-center text-sm font-bold text-muted-foreground space-y-2" data-testid="intraday-empty">
        <Clock className="w-6 h-6 mx-auto opacity-50" />
        {isCurrentlyPreMarket ? (
          <>
            <p>No pre-market prints yet for {(ticker || '').toUpperCase()} today.</p>
            <p className="text-xs">
              Minute bars only appear when trades actually occur — thinly-traded stocks
              often have zero prints for long stretches before regular market opens at 9:30 AM ET.
              The chart will fill in as trades happen or when liquidity returns at open.
            </p>
          </>
        ) : (
          <>
            <p>No trades yet today.</p>
            <p className="text-xs">The market may not have opened, or this ticker hasn&apos;t traded yet (pre-market starts 4:00 AM ET, regular session 9:30 AM ET).</p>
          </>
        )}
      </div>
    );
  }

  // Session boundary minutes (relative to 4am ET):
  //   Pre-market = 0 → 330  (4:00 - 9:30)
  //   Regular    = 330 → 720 (9:30 - 16:00)
  //   After-hrs  = 720 → 960 (16:00 - 20:00)
  return (
    <div className="space-y-3 p-3 sm:p-4" data-testid="intraday-tab">
      {/* Local CSS for the active-session pulse (kept inline so we don't
          pollute global styles). Targets the <rect> Recharts renders
          inside ReferenceArea — `.recharts-reference-area-rect` is the
          stable internal class. */}
      <style>{`
        @keyframes intraday-session-pulse {
          0%, 100% { fill-opacity: 0.08; }
          50%      { fill-opacity: 0.22; }
        }
        .intraday-active-band .recharts-reference-area-rect {
          animation: intraday-session-pulse 2.4s ease-in-out infinite;
        }
      `}</style>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Clock className="w-4 h-4 text-violet-500" />
          <h3 className="text-sm font-bold">Today · Minute Bars (ET)</h3>
          {activeBand && !headerInfo.isFrozen && (
            <span
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide"
              style={{ backgroundColor: `${activeBand.color}22`, color: activeBand.color }}
              data-testid="intraday-active-session-badge"
              title={`You are currently in the ${activeBand.label} window`}
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ backgroundColor: activeBand.color, animation: 'intraday-session-pulse 1.6s ease-in-out infinite' }}
              />
              {activeBand.label} · Live
            </span>
          )}
          {headerInfo.isFrozen && (
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 cursor-help"
              title="Trading session has ended for this day — the chart is frozen and will refresh after 7am ET tomorrow."
              data-testid="intraday-frozen-badge"
            >
              Frozen
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] font-bold text-muted-foreground [&>span]:whitespace-nowrap">
          <div className="inline-flex items-center rounded-full border bg-muted/40 p-0.5" data-testid="intraday-chart-mode">
            {[['line', 'Line', Activity], ['candles', 'Candles', CandlestickChart]].map(([key, lbl, Icon]) => (
              <button
                key={key}
                type="button"
                onClick={() => setChartMode(key)}
                aria-pressed={chartMode === key}
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold transition-colors ${chartMode === key ? 'bg-violet-600 text-white' : 'text-muted-foreground hover:text-foreground'}`}
                title={key === 'candles' ? `${candleInterval}-minute open/high/low/close candles` : 'Closing price each minute'}
                data-testid={`intraday-chart-mode-${key}`}
              >
                <Icon className="w-3 h-3" />
                {lbl}
              </button>
            ))}
          </div>
          {chartMode === 'candles' && (
            <div className="inline-flex items-center rounded-full border bg-muted/40 p-0.5" data-testid="intraday-candle-size" title="Candle size">
              {[1, 5, 15, 30].map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => pickInterval(v)}
                  aria-pressed={candleInterval === v}
                  className={`rounded-full px-2 py-0.5 text-[11px] font-semibold transition-colors ${candleInterval === v ? 'bg-slate-700 dark:bg-slate-200 text-white dark:text-slate-900' : 'text-muted-foreground hover:text-foreground'}`}
                  data-testid={`intraday-candle-size-${v}`}
                >
                  {v}m
                </button>
              ))}
            </div>
          )}
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-sm bg-amber-200 dark:bg-amber-900/40" /> Pre-Mkt
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-sm bg-emerald-200 dark:bg-emerald-900/40" /> Regular
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-sm bg-sky-200 dark:bg-sky-900/40" /> After-Hrs
          </span>
          {priorBars.length > 0 && (
            <button
              type="button"
              onClick={() => setShowPrior((v) => !v)}
              className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border transition-colors ${
                showPrior
                  ? 'border-slate-400/50 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200'
                  : 'border-transparent text-muted-foreground hover:bg-muted/50'
              }`}
              data-testid="intraday-toggle-prior"
              title={showPrior ? 'Hide yesterday' : 'Show yesterday'}
            >
              <span className="w-3 h-0.5 rounded-sm bg-slate-400 dark:bg-slate-500" />
              <span>vs. yesterday</span>
            </button>
          )}
        </div>
      </div>

      {/* Round P112g (Jul 31, 2026) — Sparse pre-market liquidity note.
          Shown when a ticker has <10 pre-market prints AND either
          we're currently in pre-market OR at least one print exists
          (so the note explains the gaps in the pre-market portion of
          the chart). Non-dismissible; the info is small and directly
          references the actual bar count so it's honest, not noisy. */}
      {preMarketIsSparse && (
        <div
          className="flex items-start gap-2 px-3 py-2 rounded-md border border-amber-500/40 bg-amber-50 dark:bg-amber-950/30 text-amber-900 dark:text-amber-100 text-[11px]"
          data-testid="intraday-sparse-premarket-note"
        >
          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <div>
            <div className="font-bold">
              Sparse pre-market activity — only {preMarketBars} print{preMarketBars === 1 ? '' : 's'} recorded {isCurrentlyPreMarket ? 'so far' : 'today'}.
            </div>
            <div className="text-[10px] text-amber-800/90 dark:text-amber-200/90 mt-0.5">
              Minute bars only exist when a trade actually occurs in that minute. Thinly-traded tickers often have 30+ minute gaps in pre-market. The chart will fill in as trades happen{isCurrentlyPreMarket ? ' or once regular session opens at 9:30 AM ET.' : '.'}
            </div>
          </div>
        </div>
      )}
      {headerInfo.dateLabel && (
        <p className="text-[11px] font-bold text-muted-foreground" data-testid="intraday-data-date">
          Data for <span className="font-semibold text-foreground">{headerInfo.dateLabel}</span>
          {' · '}Window 4:00 AM – 8:00 PM ET
        </p>
      )}
      {/* Round 86c (May 2026) — Conversion chip for logged-out visitors
          who land here from Google. The Today tab is anonymous-
          accessible so this is often the first interactive moment a
          new visitor has with the app — we catch them at peak
          engagement. Click flow:
            1. Stash a deep-link in sessionStorage so a Google round-
               trip returns the user to THIS stock modal on the My
               Portfolio tab.
            2. Call `onSignInClick` which opens the global LoginModal
               (handles both Google OAuth and email/password) without
               leaving the modal. Email/password login resolves in-
               place; Google bounces through auth and the deep-link
               re-opens the modal on landing. */}
      {isLoggedOut && onSignInClick && (
        <button
          type="button"
          onClick={() => {
            try {
              const t = String(ticker || '').toUpperCase();
              if (t) sessionStorage.setItem('post_login_redirect', `/?stock=${encodeURIComponent(t)}&tab=my-portfolio`);
            } catch (e) { /* ignore */ }
            onSignInClick();
          }}
          data-testid="intraday-signin-chip"
          className="group w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-violet-200 dark:border-violet-900/50 bg-gradient-to-r from-violet-50/80 to-fuchsia-50/40 dark:from-violet-950/30 dark:to-fuchsia-950/20 hover:from-violet-100 dark:hover:from-violet-950/60 transition-colors text-left"
        >
          <span className="flex items-center gap-2 text-[12px]">
            <span className="inline-flex w-6 h-6 rounded-full bg-violet-500/15 dark:bg-violet-500/25 items-center justify-center">
              <Briefcase className="w-3.5 h-3.5 text-violet-600 dark:text-violet-300" />
            </span>
            <span className="font-semibold text-violet-700 dark:text-violet-200">
              Sign in to add {ticker || 'this stock'} to your portfolio
            </span>
          </span>
          <span className="text-[11px] font-bold uppercase tracking-wide text-violet-600 dark:text-violet-300 opacity-80 group-hover:opacity-100">
            Free →
          </span>
        </button>
      )}
      {sessionStats && (
        <div className="flex flex-wrap items-center gap-2" data-testid="intraday-session-row">
          <div className="flex flex-wrap items-center justify-center gap-1.5 flex-1 min-w-0 text-[11px]" data-testid="intraday-session-stats">
            {[
              ['Open', `$${sessionStats.open.toFixed(2)}`, `First print ${sessionStats.openLabel} ET`],
              ['High', `$${sessionStats.high.toFixed(2)}`, 'Highest trade today (all sessions)'],
              ['Low', `$${sessionStats.low.toFixed(2)}`, 'Lowest trade today (all sessions)'],
              sessionStats.rangePct != null && ['Range', `${Math.round(sessionStats.rangePct)}%`, `Latest print $${sessionStats.last.toFixed(2)} sits ${Math.round(sessionStats.rangePct)}% of the way from today's low to today's high (0% = at the low, 100% = at the high)`, sessionStats.rangePct],
              ['Volume', fmtVol(sessionStats.volume), 'Shares traded today (all sessions)'],
              sessionStats.priorClose > 0 && ['vs Close', `${sessionStats.last >= sessionStats.priorClose ? '+' : ''}${(((sessionStats.last - sessionStats.priorClose) / sessionStats.priorClose) * 100).toFixed(2)}%`, `Latest print vs yesterday's close $${sessionStats.priorClose.toFixed(2)} (the grey reference line)`, sessionStats.last >= sessionStats.priorClose ? 100 : 0],
            ].filter(Boolean).map(([k, v, tip, pct]) => (
              <span key={k} className="inline-flex items-center gap-1 rounded-full border bg-muted/40 px-2 py-0.5 whitespace-nowrap cursor-help" title={tip} data-testid={`intraday-stat-${k.toLowerCase().replace(/\s+/g, '-')}`}>
                <span className="text-muted-foreground font-bold uppercase tracking-wide text-[9px]">{k}</span>
                {k === 'Range' && (
                  <span className="relative inline-block w-10 h-1.5 rounded-full bg-gradient-to-r from-rose-400 via-slate-300 to-emerald-400 dark:from-rose-500 dark:via-slate-600 dark:to-emerald-500" aria-hidden="true">
                    <span className="absolute -top-0.5 w-1 h-2.5 rounded-sm bg-foreground" style={{ left: `calc(${Math.max(0, Math.min(100, pct))}% - 2px)` }} />
                  </span>
                )}
                <span className={`font-mono font-semibold ${k === 'vs Close' ? (pct === 100 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400') : ''}`}>{v}</span>
              </span>
            ))}
          </div>
          <label className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-bold cursor-pointer select-none" data-testid="intraday-vwap-toggle">
            <AbbrTip term="VWAP" className="inline-flex text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300 no-underline">
              <HelpCircle className="w-3.5 h-3.5" />
            </AbbrTip>
            <span style={{ color: showVwap ? VWAP_COLOR : undefined }}>VWAP</span>
            <input
              type="checkbox"
              checked={showVwap}
              onChange={(e) => setShowVwap(e.target.checked)}
              className="h-3.5 w-3.5 accent-amber-500 cursor-pointer"
              data-testid="intraday-vwap-checkbox"
            />
          </label>
        </div>
      )}
      <div
        className="h-72 w-full touch-pan-y select-none"
        data-swipe-lock="true"
        data-testid="intraday-chart-swipe-lock"
        title="Drag your finger across the chart to inspect prices"
      >
        <TapTooltipChart className="h-full">
        <ResponsiveContainer>
          <LineChart data={chartData} margin={{ top: 24, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
            {/* Session bands */}
            <ReferenceArea x1={0}   x2={330} fill="rgb(251 191 36)" fillOpacity={0.06} />
            <ReferenceArea x1={330} x2={720} fill="rgb(16 185 129)" fillOpacity={0.06} />
            <ReferenceArea x1={720} x2={960} fill="rgb(14 165 233)" fillOpacity={0.06} />
            {/* Active-session pulse (overlays the current band with an
                animated fill-opacity — visible without obscuring the
                price line). Hidden when the chart is frozen. */}
            {activeBand && !headerInfo.isFrozen && (
              <ReferenceArea
                x1={activeBand.x1}
                x2={activeBand.x2}
                fill={activeBand.color}
                fillOpacity={0.15}
                className="intraday-active-band"
                ifOverflow="visible"
              />
            )}
            {/* Session boundary lines */}
            <ReferenceLine x={330} stroke="rgb(16 185 129)" strokeOpacity={0.4} strokeDasharray="3 3" />
            <ReferenceLine x={720} stroke="rgb(14 165 233)" strokeOpacity={0.4} strokeDasharray="3 3" />
            {/* Yesterday's close — thin grey reference so gaps up/down read at a glance. */}
            {sessionStats?.priorClose > 0 && (
              <ReferenceLine
                y={sessionStats.priorClose}
                stroke="rgb(148 163 184)"
                strokeWidth={1}
                strokeDasharray="2 3"
                ifOverflow="extendDomain"
                label={{ value: `Prev close $${sessionStats.priorClose.toFixed(2)}`, position: 'insideBottomRight', fill: 'rgb(148 163 184)', fontSize: 10, fontWeight: 700, offset: 4 }}
              />
            )}
            {/* "Now" vertical indicator — shows the live cursor on the
                chart so users can quickly see where the current minute
                falls. Hidden outside the trading window or when frozen. */}
            {nowMins != null && !headerInfo.isFrozen && (
              <ReferenceLine
                x={nowMins}
                stroke="rgb(124 58 237)"
                strokeWidth={1.5}
                strokeDasharray="4 3"
                label={{ value: 'Now', position: 'insideTop', fill: 'rgb(124 58 237)', fontSize: 10, fontWeight: 700, offset: 4 }}
                ifOverflow="visible"
              />
            )}
            {/* Round 86 (May 2026) — We intentionally do NOT plot the
                live snapshot price as a ref-line. Headline already
                shows live $; multiple price labels on one viewport
                creates "is the price $X, $Y, or $Z?" confusion when
                cache staleness misaligns the two feeds. Chart stays
                sacred for actual prints only. */}
            <XAxis
              dataKey="mins"
              type="number"
              domain={[0, 960]}
              ticks={[0, 180, 330, 480, 600, 720, 840, 960]}
              tickFormatter={formatX}
              tick={{ fontSize: 10 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              domain={yDomain}
              tick={{ fontSize: 10 }}
              tickFormatter={(v) => `$${Number(v).toFixed(2)}`}
              width={55}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 8 }}
              labelStyle={{ color: '#0f172a', fontWeight: 700 }}
              formatter={(v, name, item) => {
                // Recharts 3 passes the series `name` prop here; use dataKey to be safe.
                const key = item?.dataKey || name;
                if (key === 'close' && chartMode === 'candles') {
                  const c = candleByMins.get(Math.floor((item?.payload?.mins ?? 0) / candleInterval) * candleInterval);
                  if (c) return [`O $${c.o.toFixed(2)} · H $${c.h.toFixed(2)} · L $${c.l.toFixed(2)} · C $${c.c.toFixed(2)} · Vol ${fmtVol(c.v)}`, `${candleInterval}m candle`];
                }
                if (key === 'close') {
                  const c = candleByMins.get(Math.floor((item?.payload?.mins ?? 0) / candleInterval) * candleInterval);
                  return [`$${Number(v).toFixed(4)}${c ? ` · ${candleInterval}m vol ${fmtVol(c.v)}` : ''}`, 'Today'];
                }
                if (key === 'priorClose') return [`$${Number(v).toFixed(4)}`, `Yesterday${priorDate ? ' (' + priorDate + ')' : ''}`];
                if (key === 'vwap') return [`$${Number(v).toFixed(4)}`, 'VWAP'];
                return [v, name];
              }}
              labelFormatter={(mins) => {
                if (chartMode === 'candles') {
                  const start = Math.floor(mins / candleInterval) * candleInterval;
                  return `${formatX(start)} – ${formatX(Math.min(start + candleInterval, 960))} ET`;
                }
                return `${formatX(mins)} ET`;
              }}
            />
            {/* Round 86b (May 2026) — Yesterday ghost line. Drawn
                BEFORE today's line so today renders on top. Thin
                slate-grey, dashed, no dots, no animation. Hidden
                when user toggles it off or when no prior bars
                returned (e.g. brand-new ticker, weekend). */}
            {showPrior && priorBars.length > 0 && (
              <Line
                type="monotone"
                dataKey="priorClose"
                stroke="rgb(148 163 184)"
                strokeWidth={1}
                strokeDasharray="3 3"
                strokeOpacity={0.85}
                dot={false}
                isAnimationActive={false}
                connectNulls
                name="Yesterday"
              />
            )}
            {showVwap && (
              <Line
                type="monotone"
                dataKey="vwap"
                stroke={VWAP_COLOR}
                strokeWidth={1.5}
                strokeDasharray="6 3"
                dot={false}
                isAnimationActive={false}
                connectNulls
                name="VWAP"
              />
            )}
            <Line
              type="monotone"
              dataKey="close"
              stroke="rgb(124 58 237)"
              strokeWidth={chartMode === 'candles' ? 0 : 1.5}
              strokeOpacity={chartMode === 'candles' ? 0 : 1}
              dot={false}
              activeDot={chartMode !== 'candles'}
              isAnimationActive={false}
              connectNulls
              name="Today"
            />
            {chartMode === 'candles' && <Candles candles={candles} interval={candleInterval} />}
          </LineChart>
        </ResponsiveContainer>
        </TapTooltipChart>
      </div>
      {maxVol > 0 && (
        <div className="w-full -mt-1" data-testid="intraday-volume-strip" title={`Volume per ${candleInterval}-minute bucket`}>
          <div className="h-14 w-full">
          <ResponsiveContainer>
            <LineChart data={chartData} margin={{ top: 2, right: 10, left: 0, bottom: 0 }}>
              <XAxis dataKey="mins" type="number" domain={[0, 960]} hide />
              <YAxis domain={[0, maxVol]} width={55} tick={false} axisLine={false} tickLine={false} />
              <ReferenceLine x={330} stroke="rgb(16 185 129)" strokeOpacity={0.4} strokeDasharray="3 3" />
              <ReferenceLine x={720} stroke="rgb(14 165 233)" strokeOpacity={0.4} strokeDasharray="3 3" />
              <VolumeBars buckets={volumeBuckets} interval={candleInterval} maxVol={maxVol} />
            </LineChart>
          </ResponsiveContainer>
          </div>
          <p className="text-[10px] font-bold text-muted-foreground pl-[55px] mt-0.5">Volume · {candleInterval}m buckets · peak {fmtVol(maxVol)} · pre/after-hours bars shown lighter</p>
        </div>
      )}
      <p className="text-[11px] font-bold text-muted-foreground flex items-start gap-1.5">
        <Info className="w-3 h-3 mt-0.5 shrink-0" />
        <span>
          Bars are 1-minute Massive aggregates with 15-minute delay (only minutes where actual trades printed).{chartMode === 'candles' ? ` Candles group them into ${candleInterval}-minute open/high/low/close; green closed above its open, red below.` : ''}
          The live quote in the header above can differ from the latest bar on illiquid stocks that haven't traded recently.
          Pre-market window 4–9:30am ET, regular session 9:30am–4pm ET, after-hours 4–8pm ET.
        </span>
      </p>
      {points.length > 0 && (() => {
        const shareInterval = chartMode === 'candles' ? candleInterval : 5;
        const shareQs = `interval=${shareInterval}&vwap=${showVwap ? 1 : 0}`;
        // As-of stamp → unique share URL per snapshot so X re-crawls instead of reusing a stale/failed card.
        const asOf = `${(dataDate || '').replace(/[^0-9]/g, '')}-${sessionStats ? Math.round(sessionStats.last * 100) : 0}-${points.length}`;
        return (
        <div className="flex flex-wrap items-center gap-2" data-testid="intraday-share-row">
          <ShareToXButton
            variant="button"
            ticker={`today-${ticker}`}
            url={`https://twylightcrow.com/api/share/og-page/today/${encodeURIComponent(String(ticker || '').toUpperCase())}?${shareQs}&v=${asOf}`}
            text={`${String(ticker || '').toUpperCase()} today${sessionStats ? ` — $${sessionStats.last.toFixed(2)}${sessionStats.priorClose > 0 ? ` (${sessionStats.last >= sessionStats.priorClose ? '+' : ''}${(((sessionStats.last - sessionStats.priorClose) / sessionStats.priorClose) * 100).toFixed(2)}% vs prev close)` : ''}, range $${sessionStats.low.toFixed(2)}–$${sessionStats.high.toFixed(2)}, vol ${fmtVol(sessionStats.volume)}` : ''}. Minute candles + VWAP on DiviDrip:`}
            hashtags={['stocks', 'dividends']}
            label="Share chart"
          />
          <a
            href={`${API}/stocks/${encodeURIComponent(String(ticker || '').toUpperCase())}/intraday/og.png?download=1&${shareQs}`}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs border border-border hover:border-sky-500/40 hover:text-sky-500 transition-colors"
            title={`Download today's chart card as a PNG (${shareInterval}-min candles${showVwap ? ', with VWAP' : ''})`}
            data-testid="intraday-download-png"
          >
            <Download className="w-3.5 h-3.5" />
            Download PNG
          </a>
          <span className="text-[10px] text-muted-foreground" data-testid="intraday-share-settings">{shareInterval}m candles{showVwap ? ' · VWAP on' : ''}</span>
        </div>
        );
      })()}
    </div>
  );
}
